import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useModalStack, Z_INDEX_SHEET, Z_INDEX_STEP } from "@/lib/modal-stack";

const Sheet = SheetPrimitive.Root;
const FALLBACK_SHEET_TITLE = "Sheet dialog";

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, style, ...props }, ref) => (
  <SheetPrimitive.Overlay
    // data-slot lets outside-click guards (PostForm's shouldIgnoreClick,
    // modal-stack's isInsideRadixPortal) recognise a Sheet overlay so a
    // Sheet opened over a modal/form doesn't dismiss the layer beneath it.
    data-slot="sheet-overlay"
    className={cn(
      "fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:fill-mode-forwards data-[state=closed]:pointer-events-none",
      className
    )}
    style={style}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed gap-4 bg-white p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fill-mode-forwards",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
      openDuration: {
        fast: "data-[state=open]:duration-300",
        normal: "data-[state=open]:duration-500",
        slow: "data-[state=open]:duration-700",
      },
      closedDuration: {
        fast: "data-[state=closed]:duration-300",
        normal: "data-[state=closed]:duration-500",
        slow: "data-[state=closed]:duration-700",
      },
    },
    defaultVariants: {
      side: "right",
      openDuration: "normal",
      closedDuration: "fast",
    },
  }
);

interface SheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  overlayClassName?: string;
  hideOverlay?: boolean;
  showCloseButton?: boolean;
  /**
   * Extra z-index added on top of the depth-derived value. Mirrors Modal's
   * `zIndexOffset`. A Sheet's base (Z_INDEX_SHEET) sits one step below a
   * Modal's, so a Sheet nested one level under a Modal only reaches parity;
   * pass `Z_INDEX_STEP` to lift it a clear step above the parent modal.
   */
  zIndexOffset?: number;
  /** to allow add overlay when modal is false */
  customOverlay?:
    React.ReactNode | ((params: { zIndex: number }) => React.ReactNode);
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = "right",
      openDuration,
      closedDuration,
      className,
      children,
      overlayClassName,
      hideOverlay = false,
      showCloseButton = true,
      customOverlay,
      zIndexOffset = 0,
      ...props
    },
    ref
  ) => {
    const depth = useModalStack();
    const zIndex =
      Z_INDEX_SHEET + (depth > 0 ? depth * Z_INDEX_STEP : 0) + zIndexOffset;
    const needsFallbackTitle = !hasSheetTitle(children);

    return (
      <SheetPortal>
        {customOverlay ? (
          typeof customOverlay === "function" ? (
            customOverlay({ zIndex })
          ) : (
            customOverlay
          )
        ) : hideOverlay ? null : (
          <SheetOverlay className={overlayClassName} style={{ zIndex }} />
        )}
        <SheetPrimitive.Content
          ref={ref}
          data-slot="sheet-content"
          className={cn(
            sheetVariants({ side, openDuration, closedDuration }),
            className
          )}
          style={{ zIndex }}
          {...props}
        >
          {showCloseButton && (
            <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-neutral-100">
              <i className="fa-solid fa-xmark h-4 w-4" />
              <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
          )}
          {needsFallbackTitle ? (
            <SheetTitle className="sr-only">{FALLBACK_SHEET_TITLE}</SheetTitle>
          ) : null}
          {children}
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  }
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-2", className)} {...props} />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    data-slot="sheet-title"
    className={cn("text-lg font-semibold text-neutral-900", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

function hasSheetTitle(children: React.ReactNode): boolean {
  return React.Children.toArray(children).some((child) => {
    if (!React.isValidElement(child)) {
      return false;
    }

    if (
      child.type === SheetTitle ||
      child.type === SheetPrimitive.Title ||
      child.props["data-slot"] === "sheet-title"
    ) {
      return true;
    }

    const nestedChildren =
      "children" in child.props
        ? (child.props as { children?: React.ReactNode }).children
        : undefined;

    return nestedChildren != null && hasSheetTitle(nestedChildren);
  });
}

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-neutral-500", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
